import { DEBUG } from '@src/DEBUG';
DEBUG.open ( '/service/plugin-manager.ts' );
import { Injectable } from '@angular/core';
import { Constants } from '../commons/Constants';
import { Annotation, FourdCesiumHelper, FourDService, Transform } from '4d-mapper';
import { environment } from '@src/environments/environment';
import { WMSTransformRenderer } from '../v2/commons/cesium/cesium-renderer-wms';
import { XsEventListenerInterface } from '../xs/interfaces/event-listener.interface';
import { XsIdentityInterface } from '../xs/interfaces/identify.interface';
import { XsEventify } from '../xs/services/events/eventify.class';
import { XsApplicationEventsService } from '../xs/services/events/application-events.service';
import { Public3DRenderer } from '../v2/commons/cesium/public-3d-renderer';
import { AnnotationRenderer } from '../v2/commons/cesium/annotation-renderer';
import { MeasurementHelperService } from './measurement-helper.service';
import { SceneControllerService } from './controllers/scene-controller.service';
import { ArcGisTransformRenderer } from '../v2/commons/cesium/cesium-renderer-arcgis';
import { WFSTransformRenderer } from '../v2/commons/cesium/cesium-renderer-wfs';
import { WMTSTransformRenderer } from '../v2/commons/cesium/cesium-renderer-wtms';

const Cesium = (<any>window).Cesium;


@Injectable({
  providedIn: 'root'
})
export class CesiumMapService implements XsIdentityInterface, XsEventListenerInterface {

  __ID      = null;
  __EVENTS  = null;
  
  debug:boolean = Constants.debug_off;
  debugCounter:number = 0;


  // the element used for the Cesium map
  // canvas: HTMLElement = null;

  // our Cesium viewer
  // viewer:any = null;

  // imagery provider for Cesium ellipsoid
  // imageryProvider:any = null;

  // 4DMapper Cesium helper
  // helper:FourdCesiumHelper = null;

  // all our globels / maps
  globes:any = {};

  interval:any = null;
  realtime:any = null;
  mousePosition:any = null;

  constructor ( public fourD: FourDService, public applicationEvents: XsApplicationEventsService, public measureHelper: MeasurementHelperService, public sceneController: SceneControllerService ) {

    DEBUG.log ( '/service/map-viewer.service.ts' );

    this.__ID = XsIdentityInterface.__init(this);
    this.__EVENTS = XsEventListenerInterface.__init(this);

    if ( this.debug ) { console.log ( "%c map-viewer.service CONSTRUCTOR()", Constants._yellow ); }

    let me = this;

    document.addEventListener('mousemove', (event) => {
      me.mousePosition = Math.round(event.clientX + event.clientY);
    });

    this.interval = setInterval( () => {
      Object.keys(this.globes).forEach( key => {
        this.globes[key].timePulse = 10;
        // var now = Cesium.getTimestamp();
        this.globes[key].timeLast = this.globes[key].timeNow;
        this.globes[key].mouseLast = this.globes[key].mouseNow;
        this.globes[key].timeNow = this.globes[key].viewer.scene._frameState.frameNumber; // Cesium.getTimestamp();
        this.globes[key].mouseNow = this.mousePosition;
        // this.globes[key].timeLast = this.globes[key].viewer.camera.timeSinceMoved; // _lastCameraMoveTime;
        this.globes[key].timeSince = this.globes[key].timeNow - this.globes[key].timeLast; // now - this.globes[key].viewer._lastCameraMoveTime;
        if ( this.globes[key].mouseNow != this.globes[key].mouseLast ) {
          if ( this.globes[key].timeSince < 1 ) {
            this.globes[key].timeDown++;
          } else {
            this.globes[key].timeDown = 0;
          }
        }
        // console.log('!!!!',this.globes[key].viewer);
        // if (    !cameraMovedInLastSecond &&    !cesium.viewer.clock.shouldAnimate  ) {    if (cesium.verboseRendering) {      console.log("stopping rendering @ " + getTimestamp());    }    cesium.viewer.useDefaultRenderLoop = false;    // custom message then hard refresh the broswer using location.reload(true);  }
      });
    }, 500);

    /* this.realtime = setInterval( () => {
      Object.keys(this.globes).forEach( key => {
        if ( this.globes[key].timePulse > 0 ) {
          this.globes[key].timePulse--;
        }
      });
    }, 500 ); */

  }

  /* inject ( viewer:any = null ) {
    if ( this.debug ) { console.log ( "%c map-viewer.service inject()", Constants._yellow ); }
    this.viewer = viewer;
  } */

  init ( name:string = 'base', canvas:HTMLElement ) {

    if ( this.debug ) { console.log ( "%c map-viewer.service init()", Constants._yellow, name, this.globes ); }

    if ( !this.globes[name] ) {
      this.globes[name] = {
        events: name == 'base' ? true : false,
        alive: false,
        timeSince:0,
        timeNow: 0,
        mouseNow: null,
        timeLast:0,
        mouseLast: null,
        timePulse:0,
        timeDown:0,
        canvas: canvas,
        viewer: null,
        imageryProvider: null,
        helper: null
      }
    }

    const map = this.globes[name];
    if ( this.debug ) { console.log ( "%c map-viewer.service init() start", Constants._yellow, name, map, this.globes ); }

    if ( map.events ) {
      this.applicationEvents.triggerInit ( 'mapViewer', null, this.__ID );
    }
    
    this.initImageryProvider(name);

    if ( map.events ) {
      this.applicationEvents.triggerStart ( 'mapViewer', null, this.__ID );
    }

    this.initViewer(name);
    this.initHelper(name);

    // also track when the scene is ready (the Scene might be a few milliseconds after 
    // the viewer is ready, so some might want to hook into this one instead)
    if ( map.events ) {
      this.isSceneReady(name);
    }
    if ( this.debug ) { console.log ( "%c map-viewer.service init() done", Constants._yellow, name, map, this.globes ); }
  }

  getMap ( name:string = 'base' ) {
    const map = this.globes[name];
    if ( map ) {
      return map;
    }
    return null;
  }

  testAttempts:number = null;
  keepTestingForScene:boolean = false;

  // Note: make this one a generic service of the Eventify() class / service

  isSceneReady( name:string = 'base', reset:boolean = true ) {
    if ( reset ) {
      this.keepTestingForScene = true;
      this.testAttempts = 15;
      if ( this.debug ) { console.log('isSceneReady() reset 5'); }
    }

    let test1=null, test2=null, test3=null;

    const map = this.globes[name];

    if ( map.viewer ) {
      test1 = map.viewer['scene'] ? true : false;
      test2 = map.viewer['dataSources'] ? true : false;
      test3 = true; // this.viewer['defaultDataSource'] ? true : false;
    }

    
    if ( !reset && test1 && test2 && test3 ) {

      if ( this.debug ) { console.log('isSceneReady() READY', this.testAttempts ); }

      if ( map.events ) {
        this.applicationEvents.triggerEnd ( 'mapViewer', { viewer:map.viewer, helper:map.helper }, this.__ID );
      }
      this.keepTestingForScene = false;

    }

    if ( this.keepTestingForScene ) {
      if ( this.testAttempts-- ) {
        let me = this;
        if ( this.debug ) { console.log('isSceneReady() have another go...', this.testAttempts, test1, test2, test3 ); }
        setTimeout ( () => { me.isSceneReady(name,false); }, 500 );
      } else {
        this.keepTestingForScene = false;
        if ( this.debug ) { console.log('giving up testing for sceneReady'); }
      }
    }
  }

  initImageryProvider( name:string = 'base' ) {

    const map = this.globes[name];

    // default imagery provider
    var mapboxApiKey = 'pk.eyJ1IjoiZ2VvbWF0aWNzIiwiYSI6IjRDSmd3UWcifQ.YEoSImxSElTq1uPwjZnrNw';

    map.imageryProvider = new Cesium.MapboxImageryProvider({
      url: 'https://api.mapbox.com/v4',
      mapId: 'mapbox.satellite',
      accessToken: mapboxApiKey,
      maximumLevel: 16,
      minimumLevel: 0
    });

    if ( this.debug ) { console.log ( "%c map-viewer.service initImageryProvider()", Constants._yellow, map.imageryProvider ); }

  }

  createReport ( name:string = 'base' ) {
    const map = this.globes[name];
    let m = {};
    ['events','alive','timeNow','timeLast','timeDown'].forEach( mp => { m[mp] = map[mp]; });
    return m;
    // this.sceneController.reportComplexAction ( 'color-manager::render-colors', { name:name, config:config, map:m });
  }

  initViewer( name:string = 'base' ) {

    const map = this.globes[name];

    const timeline:boolean = false;

    let config = {
      homeButton: false,
      sceneModePicker: false,
      navigationInstructionsInitiallyVisible: false,
      geocoder: false,
      baseLayerPicker: false,
      navigationHelpButton: false,
      fullscreenButton: false,
      
      scene3DOnly: true,
      showRenderLoopErrors: environment.production,
      imageryProvider: map.imageryProvider,

      // requestRenderMode: true,
      // maximumRenderTimeChange: Infinity,

      //NOTE: disable Cesium/leaflet timelines/animations until further notice
      timeline: timeline,
      timeControlsInitiallyVisible: timeline,
      animation: timeline,

      selectionIndicator: false,
      infoBox: false,
      /*terrainProvider: Cesium.createWorldTerrain({
        requestWaterMask : true,
        requestVertexNormals: true // true //Needed to visualize slope
      })*/
      shouldAnimate: true
    };

    try {
      map.viewer = new Cesium.Viewer( map.canvas, config );
    } catch (error) {
      console.error("Couldn't instantiate Cesium viewer", map );
    }

    this.sceneController.reportComplexAction ( 'map-viewer::init-viewer', { name:name, config:config, map:this.createReport(name) });

    // no double-click
    map.viewer.screenSpaceEventHandler.removeInputAction(Cesium.ScreenSpaceEventType.LEFT_DOUBLE_CLICK);
    
    // no credit display
    // disabled; using CSS instead to hide it, otherwise Cesium throws a mickey when destroying the viewer
    // this.viewer.scene.frameState.creditDisplay.container.remove();

    //Set the time to position of the sun
    map.viewer.clock.currentTime = Cesium.JulianDate.fromDate(new Date());
    map.viewer.clock.multiplier = 1;
    map.viewer.clock.shouldAnimate = true;

    // picking and display
    // NOTE: in the future, make this 'true' when we have a terrain, otherwise 'false' (probably in the SceneController)
    map.viewer.scene.globe.depthTestAgainstTerrain = true;
    map.viewer.scene.useDepthPicking = true;
    map.viewer.scene.pickTranslucentDepth = true;

    map.alive = true;

    if ( this.debug ) { console.log ( "%c map-viewer.service initViewer()", Constants._yellow, map ); }
  }

  initHelper( name:string = 'base' ) {
    const map = this.globes[name];
    if ( this.debug ) { console.log ( "%c map-viewer.service initHelper()", Constants._yellow, map ); }
    map.helper = new FourdCesiumHelper(this.fourD, map.viewer);

    map.helper.registerRenderer(Annotation,'*',new AnnotationRenderer(map.helper))
    map.helper.registerRenderer(Transform,'wms-remote',new WMSTransformRenderer(map.viewer))
    map.helper.registerRenderer(Transform,'arcgis-remote',new ArcGisTransformRenderer(map.viewer))
    map.helper.registerRenderer(Transform,'wfs-remote',new WFSTransformRenderer(map.viewer))
    map.helper.registerRenderer(Transform,'wmts-remote',new WMTSTransformRenderer(map.viewer))
    map.helper.registerRenderer(Transform,'public-3d',new Public3DRenderer(map.helper))


    // moved here from mapService
    // this.helper.registerRenderer(Annotation,'*',new AnnotationRenderer(this.helper))
    // this.helper.registerRenderer(Transform,'wms-remote',new WMSTransformRenderer(this.viewer))
    // this.helper.registerRenderer(Transform,'arcgis-remote',new ArcGisTransformRenderer(this.viewer))
    // this.helper.registerRenderer(Transform,'wfs-remote',new WFSTransformRenderer(this.viewer))
    // this.helper.registerRenderer(Transform,'wmts-remote',new WMTSTransformRenderer(this.viewer))
    // this.helper.registerRenderer(Transform,'public-3d',new Public3DRenderer(this.helper))
    

  }

  destroy( name:string = 'base' ) {

    let map = this.globes[name];

    if ( this.debug ) { console.log ( "%c map-viewer.service destroy()", Constants._yellow, name, map ); }
    if ( map.events ) {
      this.applicationEvents.triggerStart ( 'mapViewerDestroy', null, this.__ID );
    }

    if ( map.viewer ) {
      if ( map.viewer.entities ) {
        map.viewer.entities.removeAll();
      }
      if ( map.events ) {
        this.applicationEvents.triggerEnd ( 'mapViewerDestroy', null, this.__ID );
        map.events = null;
      }
      try {
        map.viewer.destroy();
        map.viewer = null;
      } catch (e) {
        console.log('error destroying viewer',e);
      }
    }

    map.helper = null;
    map.imageryProvider = null;
    map.canvas = null;
    map.alive = false;

    map = null;
    this.globes[name] = null;
    delete this.globes[name];

    if ( this.debug ) { console.log ( "%c map-viewer.service destroy() DESTROYED", Constants._yellow, map, this.globes ); }

  }

}
